import React, { useState, useEffect, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Index from './pages/Index';
import Admin from './pages/admin/Index';
import Dashboard from './pages/admin/Dashboard';
import QuotationView from './pages/client/QuotationView';
import QuotationPrintView from './QuotationPrintView';
import QuotationViewLite from './pages/client/QuotationViewLite';
import NotFound from './pages/NotFound';
import TestScrapePage from './pages/TestScrapePage';
import Settings from './pages/admin/Settings';
import BookingTest from './pages/BookingTest';
import FlightSearch from './pages/admin/FlightSearch';
import AmadeusHotelTest from './pages/admin/AmadeusHotelTest';
import TripForm from './pages/admin/TripForm';
import TravelHub from './pages/admin/TravelHub';
import QuotationForm from './pages/admin/QuotationForm';
import ErrorBoundary from './components/ErrorBoundary';
import Clients from './pages/admin/Clients';
import DataMigration from './pages/admin/DataMigration';
import ImageMigration from './pages/admin/ImageMigration';
import Login from './pages/admin/Login';
import ResetPassword from './pages/admin/ResetPassword';
import { supabase } from './lib/supabase/client';
import { Loader2 } from 'lucide-react';
import { PromptProvider } from './contexts/PromptContext';
import Hotels from './pages/admin/Hotels';
import PlacesSearch from './pages/admin/PlacesSearch';
import HotelSearchView from './pages/admin/HotelSearchView';
import './App.css';

// Componente para redirecionamento externo
function ExternalRedirect({ to }: { to: string }) {
  useEffect(() => {
    window.location.href = to;
  }, [to]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-voah-cream">
      <div className="text-center">
        <Loader2 className="mx-auto h-12 w-12 animate-spin text-voah-teal" />
        <p className="mt-4 text-voah-teal font-medium">Redirecionando...</p>
      </div>
    </div>
  );
}

// Componente para proteger rotas administrativas
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const location = useLocation();
  useEffect(() => {
    // Verificar autenticação
    const checkAuth = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        setAuthenticated(!!data.session);
      } catch (error) {
        console.error('Erro ao verificar autenticação:', error);
        setAuthenticated(false);
      }
    };
    checkAuth();
    // Configurar listener para mudanças na autenticação
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event: string, session: any) => {
        setAuthenticated(!!session);
      }
    );
    // Cleanup
    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);
  // Estado de carregamento enquanto verificamos a autenticação
  if (authenticated === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-voah-cream">
        <div className="text-center">
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-voah-teal" />
          <p className="mt-4 text-voah-teal font-medium">Carregando...</p>
        </div>
      </div>
    );
  }
  // Redirecionar para login se não estiver autenticado
  if (!authenticated) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }
  // Renderizar as rotas protegidas se estiver autenticado
  return <>{children}</>;
}

// Prefetch critical components
const prefetchCriticalModules = () => {
  // Prefetch AccommodationGroupSection - a component that was causing issues
  import('./components/quotation/tabs/AccommodationGroupSection')
    .catch(err => console.log('Prefetch warning (non-critical):', err));
  
  // Other critical modules can be added here
};

function App() {
  useEffect(() => {
    // Prefetch critical modules after the app is mounted
    prefetchCriticalModules();
  }, []);

  return (
    <ErrorBoundary>
      <PromptProvider>
        <Router>
          <Routes>
            <Route path="/" element={<ExternalRedirect to="https://vemvoah.com" />} />
            
            {/* Rotas de autenticação */}
            <Route path="/admin/login" element={<Login />} />
            <Route path="/admin/reset-password" element={<ResetPassword />} />
            
            {/* Rotas admin protegidas */}
            <Route path="/admin" element={
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            }>
              <Route path="" element={<Dashboard />} />
              <Route path="quotations/new" element={<QuotationForm />} />
              <Route path="quotations/:id" element={<QuotationForm />} />
              <Route path="trips/new" element={<TripForm />} />
              <Route path="trips/:id" element={<TripForm />} />
              <Route path="travel-hub" element={<TravelHub />} />
              <Route path="clients" element={<Clients />} />
              <Route path="settings" element={<Settings />} />
              <Route path="flights" element={<FlightSearch />} />
              <Route path="amadeus-hotel-test" element={<AmadeusHotelTest />} />
              <Route path="hoteis" element={<Hotels />} />
              <Route path="places-search" element={<PlacesSearch />} />
              <Route path="data-migration" element={<DataMigration />} />
              <Route path="image-migration" element={<ImageMigration />} />
              <Route path="hotel-search" element={<HotelSearchView />} />
              <Route path="hotel-search/:searchId" element={<HotelSearchView />} />
            </Route>
            
            {/* Rotas públicas */}
            <Route path="/client/quotations/:id" element={<QuotationView />} />
            <Route path="/quotation/:id" element={<QuotationView />} />
            <Route path="/quotation/:id/print" element={<QuotationPrintView />} />
            <Route path="/quotation/:id/pdf-lite" element={<QuotationViewLite />} />
            <Route path="/test-scrape" element={<TestScrapePage />} />
            <Route path="/booking-test" element={<BookingTest />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Router>
      </PromptProvider>
    </ErrorBoundary>
  );
}
export default App;